import { useEffect, useState } from "react";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Users, Crown, Shield, ShieldCheck, 
  Star, User, Award, Flame, Search, SlidersHorizontal, ArrowUpDown
} from "lucide-react";

const iconMap = {
  users: Users,
  crown: Crown,
  shield: Shield,
  "shield-check": ShieldCheck,
  star: Star,
  user: User,
  award: Award,
  flame: Flame,
};

const GroupHeaderIcon = ({ icon, color, className }) => {
  if (icon && (icon.startsWith("http") || icon.includes("/"))) {
    return <img src={icon} alt="Icon" className={`${className} object-contain`} style={{ width: 18, height: 18 }} />;
  }
  const IconCmp = iconMap[icon] || Users;
  return <IconCmp className={className} style={{ color }} />;
};

const getGroupBadgeStyle = (color) => {
  if (!color) return { backgroundColor: '#71717A', color: '#fff' };
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) || 0;
  const g = parseInt(hex.substring(2, 4), 16) || 0;
  const b = parseInt(hex.substring(4, 6), 16) || 0;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return {
    backgroundColor: color,
    color: brightness > 150 ? '#000000' : '#ffffff'
  };
};

function WeaponIcon({ name, className }) {
  if (!name) return null;
  const lower = name.toLowerCase();
  
  if (lower.includes("awp")) {
    return (
      <svg className={className} viewBox="0 0 32 12" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <rect x="0" y="4" width="22" height="1" />
        <rect x="18" y="3" width="8" height="4" rx="1" />
        <rect x="16" y="1" width="6" height="1.5" />
        <rect x="15" y="0.5" width="1" height="2.5" />
        <rect x="21" y="0.5" width="1" height="2.5" />
        <path d="M25 5 L31 7 L32 11 L29 11 L26 7 Z" />
        <circle cx="21" cy="8" r="1.5" stroke="currentColor" strokeWidth="0.5" fill="none" />
      </svg>
    );
  }
  
  if (lower.includes("ak") || lower.includes("m4") || lower.includes("famas") || lower.includes("galil")) {
    return (
      <svg className={className} viewBox="0 0 32 12" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <rect x="0" y="4" width="18" height="1" />
        <rect x="4" y="2" width="1" height="2" />
        <path d="M14 6 L12 11 L10 11 L12 6 Z" />
        <rect x="15" y="3" width="10" height="4" rx="1" />
        <rect x="25" y="4" width="6" height="3" rx="0.5" />
        <path d="M21 7 L23 10 L21 10 L19 7 Z" />
      </svg>
    );
  }
  
  return <span className={className}>🔫</span>;
}

export default function Members() {
  const [groupsData, setGroupsData] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("points");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const groupFilter = searchParams.get("group");
  const [groupName, setGroupName] = useState("");
  const [legendGroups, setLegendGroups] = useState([]);

  const load = () => {
    setLoading(true);
    api.get(`/users/by-groups?q=${encodeURIComponent(q)}&sort=${sort}`)
      .then((r) => {
        setGroupsData(r.data || []);
      })
      .catch((err) => {
        console.error("Failed to load members:", err);
        setGroupsData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  useEffect(() => { 
    load(); 
  }, [sort]);

  // Load group list for filter pills
  useEffect(() => {
    api.get("/idg/groups/legend")
      .then(r => {
        setLegendGroups(r.data || []);
        if (groupFilter) {
          const found = (r.data || []).find(g => g.id === groupFilter || g.slug === groupFilter);
          setGroupName(found?.name || "");
        }
      })
      .catch(() => {});
  }, [groupFilter]);

  const filteredGroups = groupFilter
    ? groupsData.filter((g) => g.id === groupFilter || g.slug === groupFilter)
    : groupsData;

  const totalMembersCount = groupsData.reduce((acc, g) => acc + (g.users?.length || 0), 0);

  return (
    <div className="space-y-6 max-w-[1500px] mx-auto">
      {/* 1. Hero Command Header */}
      <div className="bg-[#0b0e15] border border-white/10 p-4 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-[var(--tf-primary)]/10 via-transparent to-transparent pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <div 
            className="w-12 h-12 flex items-center justify-center text-black font-black shrink-0 shadow-lg"
            style={{ backgroundColor: "var(--tf-primary)" }}
          >
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-chakra text-2xl sm:text-3xl font-black uppercase tracking-wider text-white">
              MEMBRI COMUNITATE
            </h1>
            <p className="text-xs text-zinc-400 font-mono">
              // Directorul oficial de operatori, VIP și membri INDUNGI NETWORK
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 relative z-10">
          <div className="px-3.5 py-1.5 bg-[#07090e] border border-white/10 flex items-center gap-2 text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-zinc-400">Total Membri:</span>
            <span className="font-chakra font-bold text-white text-sm">{totalMembersCount || "..."}</span>
          </div>
        </div>
      </div>

      {/* 2. Group Filter Pills */}
      {legendGroups.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSearchParams({})}
            className={`px-3 py-1.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border shrink-0 ${
              !groupFilter 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#0b0e15] border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
            }`}
          >
            Toți Membrii
          </button>
          {legendGroups.map((lg) => {
            const isSelected = groupFilter === lg.id || groupFilter === lg.slug;
            return (
              <button
                key={lg.id}
                onClick={() => setSearchParams({ group: lg.id })}
                className={`px-3 py-1.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border shrink-0 flex items-center gap-1.5 ${
                  isSelected 
                    ? "text-black border-transparent shadow-md" 
                    : "bg-[#0b0e15] border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
                }`}
                style={isSelected ? { backgroundColor: lg.color || "var(--tf-primary)", borderColor: lg.color } : {}}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lg.color || "#fff" }} />
                <span>{lg.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* 3. Search & Sort Bar */}
      <div className="bg-[#0b0e15] border border-white/10 p-3 flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input 
            value={q} 
            onChange={(e) => setQ(e.target.value)} 
            onKeyDown={(e) => e.key === "Enter" && load()} 
            placeholder="Caută utilizator după nume..." 
            className="w-full bg-[#07090e] border border-white/10 pl-9 pr-3 py-2 text-xs font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]" 
            data-testid="members-search" 
          />
        </div>

        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
          <select 
            value={sort} 
            onChange={(e) => setSort(e.target.value)} 
            className="bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)] cursor-pointer" 
            data-testid="members-sort"
          >
            <option value="points">Top Puncte</option>
            <option value="recent">Recent Înregistrați</option>
            <option value="active">Recent Activi</option>
          </select>

          <button
            onClick={load}
            className="tf-btn-primary tf-clip-btn px-4 py-2 text-xs font-black uppercase tracking-wider"
          >
            Caută
          </button>
        </div>
      </div>

      {/* 4. Active Group Filter Notice */}
      {groupFilter && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0b0e15] border border-[var(--tf-primary)]/40 text-xs font-mono">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--tf-primary)]" />
            <span className="text-zinc-400">
              Grup activ: <strong className="text-white font-chakra">{groupName || groupFilter}</strong>
            </span>
          </div>
          <button
            onClick={() => setSearchParams({})}
            className="text-[11px] font-bold text-zinc-400 hover:text-white border border-white/10 px-2.5 py-1 hover:bg-white/5 transition-colors"
          >
            ✕ Resetează filtrul
          </button>
        </div>
      )}

      {/* 5. Members List by Groups */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="bg-[#0b0e15] border border-white/10 p-4 h-24" />
          ))}
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="bg-[#0b0e15] border border-white/10 p-12 text-center text-zinc-500 font-mono text-sm">
          Niciun membru găsit conform criteriilor de căutare.
        </div>
      ) : (
        <div className="space-y-8">
          {filteredGroups.map((group) => (
            <div key={group.id} className="space-y-3">
              {/* Group Title */}
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <div className="flex items-center gap-2.5">
                  <GroupHeaderIcon icon={group.label_icon} color={group.color} className="w-4 h-4" />
                  <h2 
                    className="font-chakra text-sm sm:text-base font-black uppercase tracking-wider" 
                    style={{ color: group.color || "var(--tf-primary)" }}
                  >
                    {group.name}
                  </h2>
                </div>
                <span className="text-zinc-500 font-mono text-xs">
                  {group.users?.length || 0} membri
                </span>
              </div>

              {/* Members Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {group.users.map((u) => {
                  return (
                    <Link 
                      key={u.id} 
                      to={`/u/${u.username}`} 
                      className="group bg-[#090c12] hover:bg-[#0e131d] border border-white/10 hover:border-[var(--tf-primary)]/60 p-3 flex items-center gap-3 transition-all duration-200 shadow-md relative overflow-hidden"
                    >
                      <Avatar user={u} size={48} hover={false} />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Username user={u} showBadge={false} asText hover={false} className="text-xs font-bold font-chakra truncate group-hover:text-[var(--tf-primary)] transition-colors" />
                          {((u.loadout?.banner_badge && u.loadout.banner_badge !== "none") || (u.vip_tier && u.vip_tier !== "none")) ? (
                            <VipBadge tier={u.loadout?.banner_badge || u.vip_tier} />
                          ) : u.group_name ? (
                            <span 
                              className="px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider select-none shrink-0" 
                              style={getGroupBadgeStyle(u.group_color)}
                            >
                              {u.group_name}
                            </span>
                          ) : null}
                        </div>

                        <div className="text-[10px] text-zinc-400 font-mono mt-1 flex items-center gap-2">
                          <span>{u.points?.toLocaleString() || 0} pts</span>
                          <span className="text-zinc-600">·</span>
                          <span>{u.message_count || 0} postări</span>
                        </div>

                        {u.favorite_weapon && (
                          <div className="text-[9px] text-emerald-400 font-mono flex items-center gap-1 mt-0.5">
                            <WeaponIcon name={u.favorite_weapon} className="w-3 h-3 text-emerald-400 fill-current" />
                            <span>{u.favorite_weapon}</span>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
